import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../redux/hooks";
import { selectSelectedLeague } from "../../redux/slice/selectedLeagueSlice";
import { getPlayerImageUrl } from "../../helper/helper";
import { IcSearch, IcTeams } from "../tournamentPage/icons";
import { Empty } from "../tournamentPage/MatchesPanel";
import { canManageDivisionsInLeague } from "../../utils/leagueAccess";
import type { TeamCtx } from "./TeamPage";
import { useRoster, type RosterPlayer } from "./rosterData";

const initials = (n: string) => n.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export const Avatar = ({ p, color, size }: { p: Pick<RosterPlayer, "name" | "image">; color: string; size: number }) => {
  const [bad, setBad] = useState(false);
  const url = getPlayerImageUrl(p.image) ?? "";
  return (
    <span className="pav" style={{ width: size, height: size, background: `${color}1F`, color, fontSize: Math.max(11, size / 3.2) }}>
      {url && !bad ? <img src={url} alt="" onError={() => setBad(true)} /> : initials(p.name)}
    </span>
  );
};

const Tile = ({ k, v }: { k: string; v: React.ReactNode }) => <div className="dtile"><b>{v}</b><small>{k}</small></div>;

const Drawer = ({ ctx, p, tags, onClose }: { ctx: TeamCtx; p: RosterPlayer; tags: string[]; onClose: () => void }) => {
  const navigate = useNavigate();
  const closeRef = useRef<HTMLButtonElement>(null);
  const bat = ctx.batting.find((b) => b.id === p.id);
  const bowl = ctx.bowling.find((b) => b.id === p.id);
  useEffect(() => {
    closeRef.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <>
      <div className="dscrim" onClick={onClose} />
      <aside className="drawer on" role="dialog" aria-modal="true" aria-label={`${p.name}, player details`}>
        <div className="dh">
          <Avatar p={p} color={ctx.color} size={56} />
          <div><h3>{p.name}</h3><span>{ctx.name}{tags.length ? ` · ${tags.map((t) => ({ C: "Captain", VC: "Vice-captain", WK: "Wicketkeeper" } as Record<string, string>)[t]).join(", ")}` : ""}</span></div>
          <button ref={closeRef} className="dx" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="db">
          <div className="dprof"><div><small>Batting</small><b>{p.bat || "—"}</b></div><div><small>Bowling</small><b>{p.bowl || "—"}</b></div></div>
          <h4>Batting in this tournament</h4>
          {bat && bat.inn > 0 ? (
            <div className="dtiles"><Tile k="Runs" v={bat.runs} /><Tile k="Average" v={bat.ave ? bat.ave.toFixed(1) : "–"} /><Tile k="Strike rate" v={bat.sr ? bat.sr.toFixed(1) : "–"} /><Tile k="Highest" v={bat.hs} /></div>
          ) : <p className="dnote">{ctx.statsLoading ? "Loading…" : "Hasn't batted yet in this tournament."}</p>}
          {(bowl || p.bowl) && (
            <>
              <h4>Bowling in this tournament</h4>
              {bowl && bowl.inn > 0 ? (
                <div className="dtiles"><Tile k="Wickets" v={bowl.wkts} /><Tile k="Overs" v={bowl.overs} /><Tile k="Economy" v={bowl.econ ? bowl.econ.toFixed(2) : "–"} /><Tile k="Maidens" v={bowl.mdn} /></div>
              ) : <p className="dnote">{ctx.statsLoading ? "Loading…" : "Hasn't bowled yet."}</p>}
            </>
          )}
        </div>
        <div className="df"><button className="btn primary" onClick={() => navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(p.id)}`)}>View full profile</button></div>
      </aside>
    </>
  );
};

const SquadTab = ({ ctx }: { ctx: TeamCtx }) => {
  const navigate = useNavigate();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const rosterQ = useRoster(ctx.ts);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<RosterPlayer | null>(null);
  const players = useMemo(() => rosterQ.data ?? [], [rosterQ.data]);
  const canAdd = ctx.canEdit || canManageDivisionsInLeague(selectedLeague);

  const tagsOf = (p: RosterPlayer) => [p.id === ctx.captainId ? "C" : "", p.id === ctx.viceId ? "VC" : "", p.id === ctx.wkId ? "WK" : ""].filter(Boolean);
  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const rank = (p: RosterPlayer) => (p.id === ctx.captainId ? 0 : p.id === ctx.viceId ? 1 : p.id === ctx.wkId ? 2 : 3);
    return players.filter((p) => !ql || p.name.toLowerCase().includes(ql)).sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [players, q, ctx.captainId, ctx.viceId, ctx.wkId]);

  const addPlayers = () => navigate("/tournaments/add-players", { state: { tournamentId: ctx.tid, leagueId: ctx.leagueId } });

  if (rosterQ.isLoading) return <div className="pgrid" aria-hidden="true">{Array.from({ length: 12 }, (_, i) => <div key={i} className="pcard skel"><span className="pav sk" style={{ width: 76, height: 76 }} /><i className="sk l1" /></div>)}</div>;
  if (rosterQ.isError) return <Empty icon={<IcSearch />} title="Couldn't load the squad" text="Check your connection and try again." acts={<button className="btn primary" onClick={() => void rosterQ.refetch()}>Try again</button>} />;
  if (players.length === 0)
    return (
      <Empty
        icon={<IcTeams />}
        title="No players added yet"
        text={canAdd ? "Add players, or ask the captain to invite players so they can join with their CricCenter account." : "The squad will appear here once the team adds its players."}
        acts={canAdd ? <button className="btn primary" onClick={addPlayers}>Add players</button> : undefined}
      />
    );

  return (
    <>
      {ctx.canEdit && !ctx.captainId && (
        <div className="notice"><span><b>No captain set.</b> The captain gets team notifications and confirms the playing XI.</span><button className="btn" onClick={ctx.openEdit}>Choose captain</button></div>
      )}
      <div className="tool tg-tool">
        <label className="search" style={{ marginLeft: 0 }}><IcSearch /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a player" aria-label="Find a player" />{q && <button className="clr" onClick={() => setQ("")} aria-label="Clear search">×</button>}</label>
        <span style={{ flex: 1 }} />
        {canAdd && <button className="btn primary" onClick={addPlayers}>+ Add players</button>}
      </div>
      <div className="days"><div className="summary" style={{ marginLeft: 0 }}>Squad · <b>{shown.length}</b>{shown.length !== players.length ? ` of ${players.length}` : ""} {players.length === 1 ? "player" : "players"}{q && <button onClick={() => setQ("")}>Clear</button>}</div></div>
      {shown.length === 0 ? (
        <Empty icon={<IcSearch />} title="No players match" text="Try another name." acts={<button className="btn primary" onClick={() => setQ("")}>Show all players</button>} />
      ) : (
        <div className="pgrid">
          {shown.map((p) => {
            const tags = tagsOf(p);
            return (
              <button key={p.id} className="pcard" onClick={() => setSel(p)} aria-label={`${p.name}, open player details`}>
                {tags.length > 0 && <span className="ptags">{tags.map((t) => <i key={t}>{t}</i>)}</span>}
                <Avatar p={p} color={ctx.color} size={76} />
                <b>{p.name}</b>
                <small>{p.bat || p.bowl || " "}</small>
              </button>
            );
          })}
        </div>
      )}
      {sel && <Drawer ctx={ctx} p={sel} tags={tagsOf(sel)} onClose={() => setSel(null)} />}
    </>
  );
};

export default SquadTab;
